import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SnowGlobe from './SnowGlobe';
import * as FiIcons from 'react-icons/fi';
import { translations } from '../translations';

const { FiX, FiCheck, FiMusic, FiLayout, FiImage } = FiIcons;

function Settings({ settings, onUpdate, onClose, language, uploadedImage, onImageUpload }) {
  const t = translations[language] || translations.en;
  
  const handleSettingChange = (key, value) => {
    onUpdate({ ...settings, [key]: value });
  };

  const themeOptions = [
    { value: 'christmas', label: t.christmas, emoji: '🎄', color: 'from-red-500 to-green-500' },
    { value: 'winter', label: t.winter, emoji: '❄️', color: 'from-blue-400 to-cyan-300' },
    { value: 'new-year', label: t.newYear, emoji: '🎊', color: 'from-yellow-400 to-pink-500' },
    { value: 'hanukkah', label: t.hanukkah, emoji: '🕎', color: 'from-blue-600 to-blue-400' }
  ];

  const bgOptions = [
    { value: 'night', label: t.night },
    { value: 'winter', label: t.winterBg },
    { value: 'aurora', label: t.aurora }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className="bg-[#1a1b2e] rounded-3xl w-full max-w-5xl shadow-2xl border border-white/10 max-h-[90vh] flex flex-col md:flex-row overflow-hidden"
    >
      {/* Sidebar / Image Section */}
      <div className="w-full md:w-1/3 flex flex-col relative bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-8">
        <div className="flex justify-between items-center md:hidden mb-6">
          <h2 className="text-xl font-bold text-white">{t.settings}</h2>
          <button onClick={onClose} className="p-2 bg-white/10 rounded-full">
            <SafeIcon icon={FiX} className="text-lg text-white" />
          </button>
        </div>
        
        <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
          <div className="flex items-center gap-2 text-white/80 font-medium uppercase tracking-wider text-xs mb-2">
            <SafeIcon icon={FiImage} /> {t.snowGlobeImage}
          </div>
          <SnowGlobe 
            uploadedImage={uploadedImage} 
            onImageUpload={onImageUpload}
            language={language}
          />
          <p className="text-sm text-white/50 px-4 leading-relaxed">
             Importez une photo pour créer un souvenir flottant unique dans votre animation.
          </p>
        </div>
      </div>

      {/* Main Settings Area */}
      <div className="w-full md:w-2/3 flex flex-col bg-[#1a1b2e]">
        {/* Header */}
        <div className="hidden md:flex justify-between items-center p-8 border-b border-white/5">
          <div className="flex items-center gap-3">
             <div className="p-2 bg-purple-500/20 rounded-lg">
                <SafeIcon icon={FiLayout} className="text-xl text-purple-400" />
             </div>
             <h2 className="text-2xl font-bold text-white tracking-tight">{t.settings}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors group">
            <SafeIcon icon={FiX} className="text-xl text-white/60 group-hover:text-white" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
          {/* Themes */}
          <section>
            <label className="flex items-center gap-2 text-white/90 text-sm font-bold uppercase tracking-wider mb-5">
               <span className="w-1 h-4 bg-pink-500 rounded-full"></span>
               {t.theme}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {themeOptions.map(theme => (
                <button
                  key={theme.value}
                  onClick={() => handleSettingChange('theme', theme.value)}
                  className={`relative p-5 rounded-2xl text-left transition-all border-2 group overflow-hidden ${
                    settings.theme === theme.value 
                      ? 'bg-white/10 border-white/20 shadow-xl' 
                      : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/10'
                  }`}
                >
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-3xl filter drop-shadow-md group-hover:scale-110 transition-transform">{theme.emoji}</span>
                      <span className={`font-bold ${settings.theme === theme.value ? 'text-white' : 'text-white/70'}`}>
                        {theme.label}
                      </span>
                    </div>
                    {settings.theme === theme.value && (
                       <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiCheck} className="text-xs text-white" />
                       </div>
                    )}
                  </div>
                  {settings.theme === theme.value && (
                    <div className={`absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r ${theme.color}`} />
                  )}
                </button>
              ))}
            </div>
          </section>

          {/* Backgrounds & Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <section>
              <label className="flex items-center gap-2 text-white/90 text-sm font-bold uppercase tracking-wider mb-5">
                 <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                 {t.backgroundColor}
              </label>
              <div className="space-y-3">
                {bgOptions.map(bg => (
                  <button
                    key={bg.value}
                    onClick={() => handleSettingChange('backgroundColor', bg.value)}
                    className={`w-full px-5 py-4 rounded-xl text-left transition-all flex items-center justify-between font-medium ${
                      settings.backgroundColor === bg.value 
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {bg.label}
                    {settings.backgroundColor === bg.value && <SafeIcon icon={FiCheck} />}
                  </button>
                ))}
              </div>
            </section>

            <section>
               <label className="flex items-center gap-2 text-white/90 text-sm font-bold uppercase tracking-wider mb-5">
                 <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
                 {t.displayOptions}
              </label>
              <div className="space-y-6 bg-white/5 p-6 rounded-2xl border border-white/5">
                <div>
                  <div className="flex justify-between mb-3">
                    <label className="text-white/80 font-medium">{t.snowflakeCount}</label>
                    <span className="text-white/40 text-sm font-mono">{settings.snowflakeCount}</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="300"
                    value={settings.snowflakeCount}
                    onChange={(e) => handleSettingChange('snowflakeCount', parseInt(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer slider hover:bg-white/20 transition-colors"
                  />
                </div>
                <div>
                  <div className="flex justify-between mb-3">
                    <label className="text-white/80 font-medium">{t.animationSpeed}</label>
                    <span className="text-white/40 text-sm font-mono">{settings.animationSpeed}x</span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={settings.animationSpeed}
                    onChange={(e) => handleSettingChange('animationSpeed', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer slider hover:bg-white/20 transition-colors"
                  />
                </div>
                <div className="flex flex-wrap gap-4 pt-2">
                  <label className="flex items-center gap-3 text-white/80 cursor-pointer select-none group bg-black/20 px-3 py-2 rounded-lg hover:bg-black/40 transition-colors">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      settings.showClock ? 'bg-pink-500 border-pink-500' : 'border-white/30 group-hover:border-white/50'
                    }`}>
                      {settings.showClock && <SafeIcon icon={FiCheck} className="text-xs text-white" />}
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.showClock}
                      onChange={(e) => handleSettingChange('showClock', e.target.checked)}
                      className="hidden"
                    />
                    {t.showClock}
                  </label>
                  <label className="flex items-center gap-3 text-white/80 cursor-pointer select-none group bg-black/20 px-3 py-2 rounded-lg hover:bg-black/40 transition-colors">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      settings.showGreeting ? 'bg-pink-500 border-pink-500' : 'border-white/30 group-hover:border-white/50'
                    }`}>
                      {settings.showGreeting && <SafeIcon icon={FiCheck} className="text-xs text-white" />}
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.showGreeting}
                      onChange={(e) => handleSettingChange('showGreeting', e.target.checked)}
                      className="hidden"
                    />
                    {t.showGreeting}
                  </label>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-white/10 bg-white/5 flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-3 bg-white text-black rounded-full font-bold hover:bg-gray-200 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
          >
            {t.applySettings}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default Settings;